import { Howl, Howler } from "howler"
import { useRef, useState, useEffect } from "react"

const cleanupHowl = (howl) => {
  if (!howl) return
  try {
    if (howl._sounds) {
      howl._sounds.forEach((s) => {
        if (s._node) {
          try {
            s._node.pause()
            s._node.src = ""
            s._node.removeAttribute("src")
            s._node.load()
          } catch (e) {
            console.warn("Error cleaning up internal Howl sound node:", e)
          }
        }
      })
    }
    if (howl._node) {
      try {
        howl._node.pause()
        howl._node.src = ""
        howl._node.removeAttribute("src")
        howl._node.load()
      } catch (e) {
        console.warn("Error cleaning up internal Howl base node:", e)
      }
    }
  } catch (e) {
    console.warn("Error doing Howl internal cleanups:", e)
  }
}

const useSound = (url, options = {}) => {
  const {
    autoPlay = true,
    interrupt = false,
    volume = 1,
    soundEnabled = true,
    html5 = false,
  } = options
  const [isPlaying, setPlaying] = useState(false)
  const [state, setState] = useState(null)
  const Sound = useRef(null)

  useEffect(() => {
    if (url && url !== "" && url !== null && soundEnabled) {
      Sound.current = new Howl({
        src: [url],
        onend: () => {
          setPlaying(false)
        },
        onplay: () => {
          setPlaying(true)
        },
        onstop: () => {
          setPlaying(false)
        },
        html5: html5,
      })
    }
    return () => {
      if (Sound.current) {
        Sound.current.off()
        cleanupHowl(Sound.current)
        Sound.current.unload()
        Sound.current = null
      }
    }
  }, [])

  const load = (url, config) => {
    try {
      if (Sound.current) {
        Sound.current.off()
        cleanupHowl(Sound.current)
        Sound.current.unload()
      }
      Sound.current = new Howl({
        src: [url],
        onend: () => {
          setPlaying(false)
        },
        onplay: () => {
          setPlaying(true)
        },
        onstop: () => {
          setPlaying(false)
        },
        onload: () => {
          if (config?.onLoad) {
            config?.onLoad({
              duration: Sound.current.duration(),
              loaded: true,
              shouldPlay: autoPlay,
              soundUrl: url,
            })
          }
          onLoad({
            duration: Sound.current.duration(),
            loaded: true,
            shouldPlay: autoPlay,
            soundUrl: url,
          })
          if (autoPlay || config?.autoPlay) {
            // before playing stop all audios
            Howler.stop()
            // Stop only THIS sound before playing, not ALL sounds
            // Sound.current?.stop()
            play()
          }
        },
        onloaderror: (id, error) => {
          onLoad({
            duration: Sound?.current?.duration(),
            loaded: false,
            should: autoPlay,
            errorId: id,
            errorMessage: JSON.stringify(error),
          })
        },
        html5: html5,
        // rate: audioSettings?.speed || 1,
        // volume: audioSettings?.volume / 100 || 1,
      })
      Sound.current.once("play", () => {
        setPlaying(true)
      })
      Sound.current.once("stop", () => {
        setPlaying(false)
      })
    } catch (error) {
      setPlaying(false)
      onLoad({
        duration: Sound?.current?.duration(),
        loaded: false,
        should: autoPlay,
        errorMessage: JSON.stringify(error),
      })
    }
  }

  const unload = () => {
    setState({
      duration: 0,
      loaded: false,
      shouldPlay: autoPlay,
      soundUrl: url,
    })
    if (Sound?.current) {
      Sound.current.off()
      cleanupHowl(Sound.current)
      Sound.current.unload()
      Sound.current = null
    }
  }

  const onLoad = (status) => {
    setState(status)
  }

  const play = () => {
    // console.log("🚀 ~ file: useSound.js:27 ~ load ~ Sound.current", Sound.current)
    if (interrupt) {
      Howler.stop()
    }
    // set volume
    Sound?.current?.volume(volume)
    Sound?.current?.play()
  }

  const stop = () => {
    Sound?.current?.stop()
  }

  return [play, { load, unload, sound: Sound.current, stop, isPlaying, state }]
}

export default useSound
